'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CustomImage } from 'components/shared';
import { Body1 } from './styleConstants';
import styles from './GradientTextSections.module.css';

const ANIMATION_SPEED = 0.9;
const TRIGGER_POINT = 0.75;

const GradientTextSections: React.FC<{ content: string }> = ({ content }) => {
  const [visibleWords, setVisibleWords] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const words = useMemo(() => content.split(' '), [content]);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const triggerPoint = windowHeight * TRIGGER_POINT;

      if (sectionTop <= triggerPoint && sectionTop + sectionHeight >= 0) {
        const scrolledPastTrigger = Math.max(0, triggerPoint - sectionTop);
        const animationZone = sectionHeight / ANIMATION_SPEED;
        const rawProgress = scrolledPastTrigger / animationZone;
        const progress = Math.min(Math.max(rawProgress, 0), 1);
        const totalWords = words.length;
        const wordsToShow = Math.floor(progress * totalWords);
        setVisibleWords(wordsToShow);
      } else if (sectionTop > triggerPoint) {
        setVisibleWords(0);
      } else if (sectionTop + sectionHeight < 0) {
        setVisibleWords(words.length);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [words]);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.backgroundImageWrapper}>
        <CustomImage
          className={styles.desktopImage}
          src="web_v2/uh-x-diesel/background/gradient-bg-v1.png"
          alt=""
          fill
          priority
          sizes="100vw"
        />
        <CustomImage
          className={styles.mobileImage}
          src="web_v2/uh-x-diesel/background/gradient-bg-mobile-v1.png"
          alt=""
          fill
          priority
          sizes="100vw"
        />
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.textContent}>
          <Body1 className={styles.paragraph}>
            {words.map((word, index) => {
              // Handle line breaks - split word if it contains \n
              if (word.includes('\n')) {
                const parts = word.split('\n');
                return (
                  <React.Fragment key={index}>
                    {parts.map((part, partIndex) => {
                      if (part.length === 0) {
                        // Empty part means it was between \n characters
                        return <br key={`${index}-br-${partIndex}`} />;
                      }
                      return (
                        <React.Fragment key={`${index}-${partIndex}`}>
                          <span
                            className={styles.word}
                            data-animated={index < visibleWords ? 'true' : undefined}
                          >
                            {part}
                          </span>
                          {partIndex < parts.length - 1 && <br />}
                          {partIndex === parts.length - 1 &&
                            index < words.length - 1 &&
                            ' '}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={index}>
                  <span
                    className={styles.word}
                    data-animated={index < visibleWords ? 'true' : undefined}
                  >
                    {word}
                  </span>
                  {index < words.length - 1 ? ' ' : ''}
                </React.Fragment>
              );
            })}
          </Body1>
        </div>
      </div>
    </section>
  );
};

export default GradientTextSections;
